import React from 'react';
import { ClockIcon } from 'lucide-react';
import { useWeeklyPromo } from '../hooks/useWeeklyPromo';
interface PromoCountdownProps {
  className?: string;
  /** Compact inline variant for bars and tight cards. */
  compact?: boolean;
  /** Boxed block sized for offer cards, placed under the buy button. */
  variant?: 'default' | 'card';
}
const pad = (n: number) => String(n).padStart(2, '0');

/** Live countdown to Sunday 11:59:59 PM ET. Fixed-width digits so nothing shifts as it ticks. */
export function PromoCountdown({
  className = '',
  compact = false,
  variant = 'default'
}: PromoCountdownProps) {
  const promo = useWeeklyPromo();
  const units = [{
    label: 'Days',
    value: promo.days
  }, {
    label: 'Hrs',
    value: promo.hours
  }, {
    label: 'Min',
    value: promo.minutes
  }, {
    label: 'Sec',
    value: promo.seconds
  }];

  // "America's 250th Birthday Sale price ends Sunday" → "Sale ends Sunday", sized for a card.
  const shortHeadline = `Sale ends ${promo.headline.split(' ').pop()}`;
  if (variant === 'card') {
    return <p className={`flex items-center justify-center gap-2 whitespace-nowrap text-center ${className}`} role="timer" aria-label={`${promo.headline}. ${promo.days} days, ${promo.hours} hours, ${promo.minutes} minutes left`} data-id="element-794" data-mp="3j3jwm3lmi3ib">
        <ClockIcon className="h-3.5 w-3.5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-795" data-mp="qceuwdxw9tj" />
        <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-grit-500" aria-hidden="true" data-id="element-796" data-mp="3057n2gqsurm7">
          {shortHeadline}
        </span>
        <span className="text-[12px] font-bold tabular-nums text-offwhite" aria-hidden="true" data-id="element-797" data-mp="346t84pdhmc7h">
          {pad(promo.days)}d {pad(promo.hours)}h {pad(promo.minutes)}m {pad(promo.seconds)}s
        </span>
      </p>;
  }
  if (compact) {
    return <span className={`inline-flex items-center gap-2 ${className}`} data-id="element-798" data-mp="ofwx1d29r31r">
        <span data-id="element-799" data-mp="tehx9chitmq7">{shortHeadline}</span>
        <span className="font-mono tabular-nums" aria-hidden="true" data-id="element-800" data-mp="tdoyoyspu3j5">
          {pad(promo.days)}:{pad(promo.hours)}:{pad(promo.minutes)}:{pad(promo.seconds)}
        </span>
      </span>;
  }
  return <div className={className} data-id="element-801" data-mp="3uvm1hows9odl">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-grit-500" data-id="element-802" data-mp="3uwdz159xxw6i">
        {promo.headline}
        {promo.subline && <span className="ml-2 text-offwhite" data-id="element-803" data-mp="8pp3sxifj9dy">· {promo.subline}</span>}
      </p>
      <div className="mt-3 flex items-start gap-2 sm:gap-3" role="timer" aria-label={`${promo.days} days, ${promo.hours} hours, ${promo.minutes} minutes left`} data-id="element-804" data-mp="8nl28m806mgu">
        {units.map((unit, index) => <React.Fragment key={unit.label} data-id="element-805" data-mp="3qvkb4bwi0hdm" data-mpi={index}>
            {index > 0 && <span className="pt-2 font-display text-2xl font-bold leading-none text-steel-500 sm:text-3xl" aria-hidden="true" data-id="element-806" data-mp="3m4byz83sahwr">
                :
              </span>}
            <span className="flex w-14 flex-col items-center border border-white/12 bg-ink-900 py-2 sm:w-16" aria-hidden="true" data-id="element-807" data-mp="2ycmz96omur5s">
              <span className="font-display text-3xl font-extrabold leading-none tabular-nums text-offwhite sm:text-4xl" data-id="element-808" data-mp="18xvuornbp0up">
                {pad(unit.value)}
              </span>
              <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-809" data-mp="1dq5io24vnquc">{unit.label}</span>
            </span>
          </React.Fragment>)}
      </div>
    </div>;
}